import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import emailjs from '@emailjs/browser';
import { FaWhatsapp } from 'react-icons/fa';
import { FiCheckCircle } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import styles from './Booking.module.css';

const WHATSAPP = 'https://wa.me/16726676878?text=Hi%20Murat%2C%20I%20would%20like%20to%20book%20an%20oil%20change%20service.';

export default function Booking() {
  const { t } = useTranslation();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [form, setForm] = useState({ user_name: '', user_email: '', service_type: '', service_date: '', message: '' });
  const [status, setStatus] = useState('idle');

  const reasons = [
    t('booking.reasons.r1'),
    t('booking.reasons.r2'),
    t('booking.reasons.r3'),
    t('booking.reasons.r4'),
    t('booking.reasons.r5'),
  ];

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    setStatus('sending');
    try {
      await emailjs.send(
        'service_88e0b63',
        'template_hg5909p',
        {
          name: form.user_name,
          email: form.user_email,
          message: `${form.service_type}\n${form.service_date}\n${form.message}`,
        },
        'wcjAXKm0FyJn6ewP7'
      );
      setStatus('success');
      setForm({ user_name: '', user_email: '', service_type: '', service_date: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="booking" className={styles.section} ref={ref}>
      <div className={styles.container}>
        <motion.div className={styles.left}
          initial={{ opacity: 0, x: -30 }} animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.7 }}>
          <span className={styles.label}><span className={styles.labelLine}/> {t('booking.label')}</span>
          <h2 className={styles.heading}>{t('booking.heading1')}<br /><span className={styles.accent}>{t('booking.heading2')}</span></h2>
          <p className={styles.text}>{t('booking.text')}</p>
          <ul className={styles.list}>
            {reasons.map(r => (
              <li key={r} className={styles.listItem}>
                <FiCheckCircle className={styles.checkIcon} size={16}/>
                <span>{r}</span>
              </li>
            ))}
          </ul>
          <a href={WHATSAPP} target="_blank" rel="noreferrer" className={styles.waBtn}>
            <FaWhatsapp size={20}/> {t('booking.preferWhatsapp')}
          </a>
        </motion.div>

        <motion.div className={styles.right}
          initial={{ opacity: 0, x: 30 }} animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.15 }}>
          <h3 className={styles.formTitle}>{t('booking.formTitle')}</h3>
          {status === 'success' ? (
            <div className={styles.success}>
              <div className={styles.successIcon}><FiCheckCircle size={32}/></div>
              <h4>{t('booking.successTitle')}</h4>
              <p>{t('booking.successText')}</p>
              <a href={WHATSAPP} target="_blank" rel="noreferrer" className={styles.waBtn2}><FaWhatsapp size={16}/> WhatsApp</a>
              <button className={styles.resetBtn} onClick={() => setStatus('idle')}>{t('booking.sendAnother')}</button>
            </div>
          ) : (
            <form className={styles.form} onSubmit={submit}>
              <div className={styles.row}>
                <div className={styles.group}>
                  <label>{t('booking.name')}</label>
                  <input name="user_name" value={form.user_name} onChange={handle} placeholder={t('booking.namePlaceholder')} required />
                </div>
                <div className={styles.group}>
                  <label>{t('booking.email')}</label>
                  <input name="user_email" type="email" value={form.user_email} onChange={handle} placeholder={t('booking.emailPlaceholder')} required />
                </div>
              </div>
              <div className={styles.row}>
                <div className={styles.group}>
                  <label>{t('booking.serviceType')}</label>
                  <select name="service_type" value={form.service_type} onChange={handle} required>
                    <option value="">{t('booking.selectService')}</option>
                    <option value="Oil Change">{t('booking.oilChange')}</option>
                    <option value="Oil Change + Basic Checkup">{t('booking.oilChangeCheckup')}</option>
                  </select>
                </div>
                <div className={styles.group}>
                  <label>{t('booking.preferredDate')}</label>
                  <input name="service_date" type="date" value={form.service_date} onChange={handle} />
                </div>
              </div>
              <div className={styles.group}>
                <label>{t('booking.specialRequests')}</label>
                <textarea name="message" value={form.message} onChange={handle} placeholder={t('booking.specialPlaceholder')} />
              </div>
              <button type="submit" className={styles.submit} disabled={status === 'sending'}>
                {status === 'sending' ? t('booking.sending') : t('booking.submit')}
              </button>
              {status === 'error' && <p className={styles.errorMsg}>{t('booking.errorMsg')}</p>}
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
